/**
 * 통합 SSO 세션 헬퍼.
 *
 * 인프라 (Estimate-App과 동일):
 *  - admin.sso_sessions store 공유
 *  - .antsnest.co.kr 도메인 쿠키 (antsnest.sid)
 *  - 모든 *.antsnest.co.kr 서비스가 같은 세션을 공유
 *
 * 본 모듈은 edu 자체 백엔드 (/api/auth/*)만 호출.
 * talk/brand 등 외부 도메인 cross-origin 호출은 더 이상 하지 않음.
 */

const SESSION_URL = '/api/auth/session';
const LOGIN_URL = '/api/auth/login';
const LOGOUT_URL = '/api/auth/logout';
const REGISTER_URL = '/api/auth/register';

let _sessionCache = null;

export const resetSessionCache = () => { _sessionCache = null; };

/**
 * 현재 SSO 세션 조회.
 *  - talk/store/etc 어디서든 로그인했다면 .antsnest.co.kr 쿠키로 자동 인식
 *  - 미로그인 시 401 → {} 반환
 */
export const checkSession = () => {
  if (_sessionCache) return _sessionCache;
  _sessionCache = fetch(SESSION_URL, { method: 'GET', credentials: 'include' })
    .then((r) => r.ok ? r.json() : {})
    .then((d) => {
      if (d && d.success && d.user) {
        return {
          State: 'LOGIN',
          UserId: d.user.user_account || d.user.user_id,
          Name: d.user.user_name,
          user: d.user,
        };
      }
      return {};
    })
    .catch(() => ({}));
  return _sessionCache;
};

/**
 * SSO 로그인 — edu /api/auth/login 호출.
 * 같은 admin.sso_users 테이블 + 공유 세션이라 talk/store 등에서도 동시 로그인 효과.
 */
export const ssoLogin = (userId, userPw) => {
  resetSessionCache();
  return fetch(LOGIN_URL, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, user_pwd: userPw }),
  })
    .then((r) => r.json().catch(() => ({ success: false, message: '로그인 실패' })));
};

/**
 * SSO 회원가입 — admin.sso_users 테이블에 직접 등록.
 * 가입 즉시 모든 SSO 서비스에서 사용 가능.
 */
export const ssoRegister = ({ user_account, user_name, user_pwd, user_ex_email, user_mobile }) => {
  return fetch(REGISTER_URL, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: user_account,
      user_pwd,
      user_name,
      user_email: user_ex_email || '',
      user_mobile: user_mobile || '',
    }),
  })
    .then((r) => r.json().catch(() => ({ success: false, message: '회원가입 실패' })));
};

/**
 * SSO 로그아웃 — edu /api/auth/logout 호출 (세션 destroy + 공유 쿠키 삭제).
 * .antsnest.co.kr 쿠키가 삭제되므로 모든 SSO 서비스에서 동시 로그아웃.
 */
export const ssoLogout = () => {
  resetSessionCache();
  return fetch(LOGOUT_URL, { method: 'GET', credentials: 'include' })
    .then((r) => r.json().catch(() => ({})))
    .catch(() => ({}));
};
